"use client";

import { createParser, useQueryState } from "nuqs";

/**
 * What the user has selected, per filter name.
 *
 * A value list for a checkbox filter, a two-element `[min, max]` for a range —
 * the same shape the legacy app put in the `filters` query parameter, so links
 * shared from the old app keep working.
 */
export type FilterSelection = string[];
export type SelectedFilters = Record<string, FilterSelection>;

/**
 * JSON in a query parameter.
 *
 * Not the prettiest URL, but the alternative — one parameter per filter — makes
 * "clear everything" a variable-length URL edit and collides with the app's own
 * parameter names the moment a tenant has a filter called `page` or `view`.
 */
/**
 * Read a filter selection out of JSON, or null if it isn't one.
 *
 * Exported because the URL isn't the only place a selection arrives from: the
 * map's toggle layer carries one per toggle as a JSON string on the tile. Sharing
 * the reader means a toggle can only ever ask for a selection the URL can carry
 * back — and both reject the same malformed input.
 */
export function parseFilters(value: string): SelectedFilters | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

    // A hand-edited or stale URL shouldn't be able to put a non-array in here
    // and crash every consumer that maps over it.
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, string[]] =>
        Array.isArray(entry[1]) && entry[1].every((v) => typeof v === "string")
    );
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  } catch {
    return null;
  }
}

const parseAsFilters = createParser<SelectedFilters>({
  parse: parseFilters,
  serialize(value) {
    return JSON.stringify(value);
  },
});

const EMPTY: SelectedFilters = {};

export function useSelectedFilters() {
  const [selected, setSelected] = useQueryState("filters", parseAsFilters);

  const update = (next: SelectedFilters) =>
    // An empty object would serialise to `{}` and sit in the URL forever.
    void setSelected(Object.keys(next).length > 0 ? next : null);

  return {
    selectedFilters: selected ?? EMPTY,

    /** Add or remove one value of a checkbox filter. */
    toggle(name: string, value: string) {
      const current = new Set(selected?.[name] ?? []);
      if (current.has(value)) current.delete(value);
      else current.add(value);

      const next = { ...selected };
      if (current.size > 0) next[name] = [...current];
      else delete next[name];
      update(next);
    },

    /** Set or clear a range filter's bounds. */
    setRange(name: string, range: [string, string] | null) {
      const next = { ...selected };
      if (range) next[name] = range;
      else delete next[name];
      update(next);
    },

    reset(name: string) {
      const next = { ...selected };
      delete next[name];
      update(next);
    },

    /**
     * Replace the whole selection at once.
     *
     * For the map's toggle layer, where one press stands for a group of POIs and
     * so brings its own complete selection rather than adding to what's there.
     */
    replace(next: SelectedFilters) {
      update(next);
    },

    resetAll() {
      void setSelected(null);
    },
  };
}
